/**
 * US state abbreviations for forms + dirty-data cleanup.
 * Store 2-letter codes in `state_abbr`; display full names in selects.
 */

export type UsStateChoice = { id: string; name: string };

/** Fifty states + DC (id = abbr stored in DB). */
export const US_STATE_CHOICES: UsStateChoice[] = [
  { id: "AL", name: "Alabama" },
  { id: "AK", name: "Alaska" },
  { id: "AZ", name: "Arizona" },
  { id: "AR", name: "Arkansas" },
  { id: "CA", name: "California" },
  { id: "CO", name: "Colorado" },
  { id: "CT", name: "Connecticut" },
  { id: "DE", name: "Delaware" },
  { id: "DC", name: "District of Columbia" },
  { id: "FL", name: "Florida" },
  { id: "GA", name: "Georgia" },
  { id: "HI", name: "Hawaii" },
  { id: "ID", name: "Idaho" },
  { id: "IL", name: "Illinois" },
  { id: "IN", name: "Indiana" },
  { id: "IA", name: "Iowa" },
  { id: "KS", name: "Kansas" },
  { id: "KY", name: "Kentucky" },
  { id: "LA", name: "Louisiana" },
  { id: "ME", name: "Maine" },
  { id: "MD", name: "Maryland" },
  { id: "MA", name: "Massachusetts" },
  { id: "MI", name: "Michigan" },
  { id: "MN", name: "Minnesota" },
  { id: "MS", name: "Mississippi" },
  { id: "MO", name: "Missouri" },
  { id: "MT", name: "Montana" },
  { id: "NE", name: "Nebraska" },
  { id: "NV", name: "Nevada" },
  { id: "NH", name: "New Hampshire" },
  { id: "NJ", name: "New Jersey" },
  { id: "NM", name: "New Mexico" },
  { id: "NY", name: "New York" },
  { id: "NC", name: "North Carolina" },
  { id: "ND", name: "North Dakota" },
  { id: "OH", name: "Ohio" },
  { id: "OK", name: "Oklahoma" },
  { id: "OR", name: "Oregon" },
  { id: "PA", name: "Pennsylvania" },
  { id: "RI", name: "Rhode Island" },
  { id: "SC", name: "South Carolina" },
  { id: "SD", name: "South Dakota" },
  { id: "TN", name: "Tennessee" },
  { id: "TX", name: "Texas" },
  { id: "UT", name: "Utah" },
  { id: "VT", name: "Vermont" },
  { id: "VA", name: "Virginia" },
  { id: "WA", name: "Washington" },
  { id: "WV", name: "West Virginia" },
  { id: "WI", name: "Wisconsin" },
  { id: "WY", name: "Wyoming" },
];

const ABBR_SET = new Set(US_STATE_CHOICES.map((c) => c.id));

/** Full name / common alias → abbr (lowercase keys). */
const NAME_TO_ABBR: Record<string, string> = Object.fromEntries(
  US_STATE_CHOICES.flatMap((c) => [
    [c.name.toLowerCase(), c.id],
    [c.id.toLowerCase(), c.id],
  ]),
);

// Extra aliases + truncations seen in production imports (e.g. "Connec").
const ALIAS_TO_ABBR: Record<string, string> = {
  "district of columbia": "DC",
  "washington dc": "DC",
  "washington d.c.": "DC",
  "washington, dc": "DC",
  "d.c.": "DC",
  "d.c": "DC",
  // Connecticut truncations from Zoho / address parse
  connec: "CT",
  connect: "CT",
  connecti: "CT",
  connectic: "CT",
  connecticu: "CT",
  // Other common short forms
  calif: "CA",
  cali: "CA",
  mass: "MA",
  penn: "PA",
  penna: "PA",
  fla: "FL",
  tex: "TX",
  wash: "WA",
  ariz: "AZ",
  colo: "CO",
  ore: "OR",
  nev: "NV",
  mich: "MI",
  minn: "MN",
  wis: "WI",
  wisc: "WI",
  ill: "IL",
  ind: "IN",
  ken: "KY",
  kent: "KY",
  tenn: "TN",
  virg: "VA",
  "n.y.": "NY",
  "n.j.": "NJ",
  "n.c.": "NC",
  "s.c.": "SC",
  "n.d.": "ND",
  "s.d.": "SD",
  "w.v.": "WV",
  "n.h.": "NH",
  "r.i.": "RI",
};

const stripPunctuation = (value: string) =>
  value.replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();

/**
 * Normalize free-text / dirty `state_abbr` to a 2-letter US code.
 * Returns null when empty or unrecognized (caller keeps original for review).
 */
export const normalizeUsStateAbbr = (
  input?: string | null,
): string | null => {
  const raw = String(input ?? "").trim();
  if (!raw) return null;

  const upper = raw.toUpperCase();
  if (raw.length === 2 && ABBR_SET.has(upper)) return upper;

  const key = stripPunctuation(raw).toLowerCase();
  if (NAME_TO_ABBR[key]) return NAME_TO_ABBR[key];
  if (ALIAS_TO_ABBR[key]) return ALIAS_TO_ABBR[key];

  // Prefix match for truncated full names (e.g. "Connec" → Connecticut)
  if (key.length >= 4) {
    const prefixHits = US_STATE_CHOICES.filter((c) =>
      c.name.toLowerCase().startsWith(key),
    );
    if (prefixHits.length === 1) return prefixHits[0].id;
  }

  return null;
};

import { normalizeUsCityName } from "@/lib/usCityName";

/**
 * For save paths: return normalized abbr, or trimmed original if unknown,
 * or null if empty.
 */
export const coerceUsStateAbbrForSave = (
  input?: string | null,
): string | null => {
  const raw = String(input ?? "").trim();
  if (!raw) return null;
  return normalizeUsStateAbbr(raw) ?? raw;
};

/** City save path: title-case + strip trailing zip/state junk. */
export const normalizeCityForSave = (input?: string | null): string | null =>
  normalizeUsCityName(input);
